import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    Input,
    QueryList,
    signal,
    ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, map, Observable } from 'rxjs';

import { RouterService } from '@ecm/router';

import { CarrouselItemViewModelMapper } from '../../business/mapper/carrousel-item-view-model-mapper';
import { CarrouselItemViewModel } from '../../business/model/carrousel-item.view-model';
import { CarrouselItemMobileComponent } from '../../components/carrousel-item-mobile/carrousel-item-mobile.component';
import { CarrouselItemRedirectLinkComponent } from '../../components/carrousel-item-redirect-link/carrousel-item-redirect-link.component';
import { CarrouselItemComponent } from '../../components/carrousel-item/carrousel-item.component';
import { CarrouselStoreService } from '../../store/carrousel-store.service';
import { SelectedLink } from '../../store/nav-display-config';

@Component({
    selector: 'ecm-carrousel',
    templateUrl: 'carrousel.component.html',
    styleUrls: ['carrousel.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CarrouselItemComponent,
        CarrouselItemRedirectLinkComponent,
        CarrouselItemMobileComponent,
        NgTemplateOutlet,
    ],
})
export class CarrouselComponent {
    @Input() public isXlScreen: boolean;
    @ViewChildren('buttonRef') public buttonRefs: QueryList<ElementRef>;

    public isSosh = toSignal(this.routerService.isSosh$);
    public isCustomerBenefits$ = toSignal(this.carrouselStore.checkAncrageCustomerBenefits$);
    public carrouselItems$ = toSignal(this.carrouselStore.items$);
    public isOrangeServices$ = toSignal(this.carrouselStore.checkAncrageOrangeServices$);
    public contracts$ = toSignal(this.carrouselStore.getOfferItems$);

    public isBloomActivated = computed(() => this.carrouselStore.isEligibleToBloom());

    public carrouselLinksItems = signal<{ type: string; selected: boolean }[]>([]);

    public carrouselLinksItemsSelected: boolean;

    protected selectedLink: SelectedLink = SelectedLink.EMPTY;
    protected readonly SelectedLink = SelectedLink;

    public constructor(
        protected route: ActivatedRoute,
        private carrouselStore: CarrouselStoreService,
        private routerService: RouterService,
        private carrouselItemViewModelMapper: CarrouselItemViewModelMapper,
    ) {
        combineLatest({
            selectedLink: this.carrouselStore.getDisplayConfig$.pipe(map((config) => config.selectedLink)),
            links: this.carrouselItemViewModelMapper.extractLinks(this.getIsSosh()),
        })
            .pipe(takeUntilDestroyed())
            .subscribe(({ selectedLink, links }) => {
                this.selectedLink = selectedLink;
                this.carrouselLinksItems.set(
                    links.map((type) => ({
                        type,
                        selected: type.split('.').pop() === selectedLink,
                    })),
                );
                this.carrouselLinksItemsSelected = this.carrouselLinksItems().some((item) => item.selected);
            });
    }

    public select(item: CarrouselItemViewModel) {
        if (item.id) {
            this.carrouselStore.selectItem(item.id, true);
        }

        this.carrouselStore.openLink(item.button);
    }

    public selectContract(index: number, cid: string) {
        const button = this.buttonRefs.toArray()[index].nativeElement;
        button.scrollIntoView({ inline: 'center' });
        if (cid) {
            this.carrouselStore.selectItem(cid);
        }
    }

    public getIsSosh(): Observable<boolean> {
        return this.routerService.isCurrentRouteSosh();
    }
}
